<template>
    <!-- The list scrolls inside a cap of its own, so however many people it names, what the screen
         says under it and its answers stay in view. The region takes focus, so a keyboard scrolls it too,
         and it carries the name: the list inside has none, so a screen reader says it once. -->
    <div class="gift-dependents-scroll" role="region" aria-label="People you protect" tabindex="0" data-testid="dependents-scroll">
        <ul class="gift-dependents" data-testid="gift-dependents">
            <!-- A dependent carries no id: the list is drawn whole for one question and never reordered
                 in place, so a row's position is its identity. -->
            <li v-for="(row, position) in rows" :key="position" class="gift-dependent" data-testid="gift-dependent">
                <span class="gift-dependent__avatar" aria-hidden="true">{{ row.monogram }}</span>
                <div class="gift-dependent__who">
                    <!-- The space between the two keeps them two words wherever the text is read
                         without this layout, a screen reader's line or a copy. -->
                    <p class="gift-dependent__line">
                        <template v-if="row.label">
                            <span class="gift-dependent__name">{{ row.label }}</span>{{ ' ' }}
                        </template>
                        <span class="gift-dependent__since">Protected since {{ row.since }}</span>
                    </p>
                    <p v-if="row.email" class="gift-dependent__email">{{ row.email }}</p>
                </div>
            </li>
        </ul>
    </div>
</template>

<script setup lang="ts">
import { formatDate } from '@privacy-bee/format/date';
import { initials, maskedPersonNames } from '@privacy-bee/format/text';
import { computed } from 'vue';
import type { GiftDependent } from './gift-dependents';

/**
 * Everyone whose protection rides on the member's subscription, one row each: who they are, the
 * address they accepted with, and how long the member has been protecting them. Someone who
 * accepted without a first name is called by their address, and someone with neither by the date
 * alone.
 */
const props = defineProps<{ dependents: readonly GiftDependent[] }>();

const rows = computed(() =>
    props.dependents.map((dependent) => {
        const { name, email, label } = maskedPersonNames(dependent);

        return { label, email, monogram: initials(name), since: formatDate(dependent.accepted_at) };
    }),
);
</script>

<style scoped>
/* Vanilla CSS spending only design-system tokens: the list renders the same in hive and checkout
   without depending on either app's build pipeline or on the type roles, which checkout does not
   load. The role values are spelled out where a role would otherwise be taken. */

/* One cap wherever the list is shown, so every screen that names these people shows the same window
   onto a long list. A length rather than a token: the system names no height for a scroller. */
.gift-dependents-scroll {
    flex: none;
    max-height: min(15rem, 30vh);
    overflow-y: auto;
    border-radius: var(--radius-md);
}

.gift-dependents {
    display: flex;
    flex-direction: column;
    margin: 0;
    padding: 0;
    list-style: none;
    border: 1px solid var(--hairline);
    border-radius: var(--radius-md);
}

.gift-dependent {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    padding: var(--space-3) var(--space-4);
}

.gift-dependent + .gift-dependent {
    border-top: 1px solid var(--hairline);
}

/* DESIGN.md's avatar in a row: initials at body size in muted ink on the sunken fill, ringed. */
.gift-dependent__avatar {
    display: flex;
    flex: none;
    align-items: center;
    justify-content: center;
    width: var(--space-10);
    height: var(--space-10);
    border-radius: var(--radius-full);
    background: var(--surface-sunken);
    box-shadow: 0 0 0 1px var(--hairline);
    color: var(--ink-muted);
    font-size: var(--text-sm);
    line-height: var(--leading-sm);
    font-weight: var(--weight-normal);
}

.gift-dependent__who {
    display: flex;
    flex-direction: column;
    min-width: 0;
}

/* The body role. */
.gift-dependent__line {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    column-gap: var(--space-2);
    margin: 0;
    font-size: var(--text-sm);
    line-height: var(--leading-sm);
    font-weight: var(--weight-normal);
    color: var(--ink-body);
}

.gift-dependent__name {
    color: var(--ink);
    font-weight: var(--weight-semibold);
    overflow-wrap: anywhere;
}

.gift-dependent__since {
    font-size: var(--text-xs);
    line-height: var(--leading-xs);
    color: var(--ink-muted);
}

/* The supporting role, in muted ink. A masked address is how the member tells two people with one
   first name apart, so it wraps rather than ending in an ellipsis that could hide the part that
   differs. */
.gift-dependent__email {
    margin: 0;
    font-size: var(--text-dense);
    line-height: var(--leading-dense);
    font-weight: var(--weight-normal);
    color: var(--ink-muted);
    overflow-wrap: anywhere;
}
</style>
